import { useState, useEffect, useRef, useMemo } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useRecoilValue } from 'recoil';
import ready from 'document-ready';
import debounce from 'lodash.debounce';

import { sandhillsRequest, repositionCookieBanner, getURLParameter, insertURLParameter, removeURLParameter, setScrollTop, setIOSScrollLock, removeIOSScrollLock, syncCanonicalUrl } from '@Components/Utilities/SandhillsUtility';
import { useWindowSize } from '@Components/Utilities/WindowSize';
import { loadable } from '@Components/Utilities/SandhillsLoadable';
import FacetedSearchMobileMenu from '@Components/Shared/FacetedSearch/FacetedSearchMobileMenu';
import Paging from '@Components/MuiWrappers/Paging';
import SimpleHomeBreadcrumb from '@Components/Shared/SimpleHomeBreadcrumb';
import TranslationText from '@Components/Utilities/TranslationText';
import FacetedSearch from '@Components/Shared/FacetedSearch/FacetedSearch';
import atoms from '@Components/Utilities/SandhillsAtoms';
import EmptyLegListing from '@Components/EmptyLegs/EmptyLegListing';
import LocationMap from '@Components/Maps/LocationMap';
import LoadingRing from '@Components/MuiWrappers/LoadingRing';
import EmptyLegAlertModal from '@Components/Shared/Modals/EmptyLegAlertModal';

const EmptyLegQuoteModal = loadable(() => import('@Components/Shared/Modals/EmptyLegQuoteModal'), true);

const EmptyLegsListingsView = (props) => {
    const currentUser = useRecoilValue(atoms.currentUser);
    const countryCode = useRecoilValue(atoms.countryCode);
    const constants = {
        AirportDetails: 'AirportDetails',
        AirportID: 'AirportID',
        Bounds: 'Bounds',
        CityID: 'CityID',
        Distance: 'Distance'
    };
    const currentSite = useRecoilValue(atoms.currentWebsite);
    const routes = useRecoilValue(atoms.routes);
    const windowSize = useWindowSize();
    const [searchData, setSearchData] = useState({
        SearchBaseURL: props.EmptyLegsUrl,
        EmptyLegs: props.EmptyLegs,
        SearchFields: props.SearchFields,
        ListingsCount: props.ListingsCountTitle,
        PageHeader: props.PageHeader,
        CurrentPage: props.PaginationModel.CurrentPage ? props.PaginationModel.CurrentPage : 1,
        TotalPages: props.PaginationModel.TotalPages,
        IsDefaultSearch: props.IsDefaultSearch,
        CategoryInfo: props.CategoryInformation,
        LocationString: props.LocationString,
        ListingLocations: [],
        ListingMapData: props.ListingMapData,
        DefaultZoom: props.DefaultZoom,
        DefaultCenter: props.DefaultCenter,
        FlightPaths: []
    });
    const [isMounted, setIsMounted] = useState(false);
    const [mapDataLoading, setMapDataLoading] = useState(true);
    const [emptyLegsSearchUrl, setEmptyLegsSearchUrl] = useState(props.EmptyLegsSearchUrl);
    const [showSearchWindow, setShowSearchWindow] = useState(false);
    const [mobileSearch, setMobileSearch] = useState(false);
    const [searchInProgress, setSearchInProgress] = useState(false);
    const [outstandingChanges, setOutstandingChanges] = useState([]);
    const [emptyLegModel, setEmptyLegModel] = useState(props.EmptyLegModel);
    const [showEmptyLegQuoteModal, setShowEmptyLegQuoteModal] = useState(false);
    const [returnFocusRef, setReturnFocusRef] = useState({});
    const locationMapRef = useRef(null);
    const lastSearchedLocationRef = useRef(null);
    const [showMapViewModal, setShowMapViewModal] = useState(false);
    const [listingPictureMap, setListingPictureMap] = useState({});
    const [locationState, setLocationState] = useState(null);
    const [showLoadingRing, setShowLoadingRing] = useState(false);
    const FAQSectionRef = useRef(null);
    const [expanded, setExpanded] = useState();
    const [showAlertModal, setShowAlertModal] = useState(false);
    const [emptyLegAlerts, setEmptyLegAlerts] = useState(props.EmptyLegAlerts ? props.EmptyLegAlerts : []);

    useEffect(() => {
        const cookieBannerResize = () => {
            repositionCookieBanner('mobile-option-bar', 786);
        };

        ready(() => {
            cookieBannerResize();
            window.addEventListener('resize', cookieBannerResize);
            if (window.outerWidth > 785) {
                document.getElementsByTagName('body')?.[0]?.classList.remove('fixed');
            }

            if (props.OpenEmptyLegModalOnLoad) {
                if (props.EmptyLegID && Array.isArray(props.EmptyLegs)) {
                    const emptyLeg = props.EmptyLegs.find(el => el.EmptyLegListingID === props.EmptyLegID);
                    if (emptyLeg) {
                        openEmptyLegQuoteModal(emptyLeg);
                    }
                }
            }

            setIsMounted(true);
        });

        fetchMapData(emptyLegsSearchUrl);

        return () => {
            window.removeEventListener('resize', cookieBannerResize);
        };
    }, []);

    useEffect(() => {

        const chunkParts = () => {
            const width = window.innerWidth;
            let chunkSize;
            if (width > 1250) {
                chunkSize = 4;
            } else if (width > 1000) {
                chunkSize = 2;
            } else {
                chunkSize = 1;
            }

            const chunks = [];
            for (let i = 0; i < searchData.EmptyLegs.length; i += chunkSize) {
                chunks.push(searchData.EmptyLegs.slice(i, i + chunkSize));
            }

            const noImageRow = chunks.map((chunk) => {
                const hasImage = chunk.some((emptyLeg) => emptyLeg.MediaModel.Media.length > 0);

                return !hasImage;
            });

            const mappedListings = searchData.EmptyLegs.map((emptyLeg, index) => {
                const chunkIndex = Math.floor(index / chunkSize);
                return noImageRow[chunkIndex];
            });

            setListingPictureMap(mappedListings);
        }

        ready(() => {
            chunkParts();
            window.addEventListener('resize', chunkParts);
        });

        return () => {
            window.removeEventListener('resize', chunkParts);
        };

    }, [searchData]);

    useEffect(function runPageSearch() {
        if (isMounted) {
            runSearch(true);
        }
    }, [searchData.CurrentPage]);

    useEffect(() => {
        if (locationState && currentSite?.Site?.IsCharterHub && locationState !== lastSearchedLocationRef.current) {
            lastSearchedLocationRef.current = locationState;
            runSearch(false, {
                fieldName: constants.Bounds,
                value: locationState
            });
        }
    }, [locationState, runSearch]);

    const buildUrl = (keepPage, pendingChanges, searchField) => {
        let searchUrl = emptyLegsSearchUrl;
        if (pendingChanges) {
            pendingChanges.forEach(field => {
                searchUrl = removeURLParameter(searchUrl, field.fieldName);

                if (field.fieldName == constants.Distance) {
                    searchUrl = removeURLParameter(searchUrl, constants.Distance);
                    searchUrl = removeURLParameter(searchUrl, constants.CityID);
                    searchUrl = removeURLParameter(searchUrl, constants.AirportID);

                    if (field.airport?.AirportCodeID > 0) {
                        searchUrl = insertURLParameter(searchUrl, constants.AirportID, field.airport.AirportCodeID);
                    }
                    if (field.airport?.AirportCityID > 0) {
                        searchUrl = insertURLParameter(searchUrl, constants.CityID, field.airport.AirportCityID);
                    }
                    if (field.distance && field.distance > 0) {
                        searchUrl = insertURLParameter(searchUrl, constants.Distance, field.distance);
                    }

                    if (!isNaN(field.airport.lat) && !isNaN(field.airport.lng)) {
                        handleSetMapCenter(field.airport.lat, field.airport.lng, field.distance);
                    }
                }
                else if (field.fieldName == constants.Bounds) {
                    if (field.value) {
                        Object.keys(field.value).forEach(key => {
                            searchUrl = removeURLParameter(searchUrl, key);
                            searchUrl = insertURLParameter(searchUrl, key, field.value[key]);
                        });
                    }
                }
                else if (field.value) {
                    searchUrl = insertURLParameter(searchUrl, field.fieldName, field.value);
                }
            });
        }

        if (searchField) {
            searchUrl = insertURLParameter(searchUrl, 'SearchProperty', searchField);
        }

        searchUrl = removeURLParameter(searchUrl, 'Page');
        if (keepPage === true) {
            searchUrl = insertURLParameter(searchUrl, 'Page', searchData.CurrentPage);
        }

        return searchUrl;
    };

    const updatePendingChangesForSearch = (pendingChanges, latestFieldValue) => {
        let newField = true;
        for (var i = 0; i < pendingChanges.length; i++) {
            if (latestFieldValue.fieldName == pendingChanges[i].fieldName) {
                pendingChanges[i].value = latestFieldValue.value;
                newField = false;
                break;
            }
        }

        if (newField) {
            pendingChanges.push(latestFieldValue);
        }

        let fields = [...searchData.SearchFields];
        for (var j = 0; j < fields.length; j++) {
            let field = fields[j];
            if (latestFieldValue.fieldName == field.FieldName && field.Type == 'Range') {
                // Update the range field in state so it doesn't do a weird re-render with the old value:
                let splitValue = latestFieldValue.value.split('*');
                field.SelectedLowerBound = splitValue[0];
                field.SelectedUpperBound = splitValue[1];
                setSearchData(previousState => {
                    return { ...previousState, SearchFields: fields };
                });
                break;
            }
        }

        return pendingChanges;
    };
    const runSearch = (keepPage, latestFieldValue) => {
        let pendingChanges = [...outstandingChanges];
        if (latestFieldValue) {
            if (Array.isArray(latestFieldValue)) {
                for (var i = 0; i < latestFieldValue.length; i++) {
                    pendingChanges = updatePendingChangesForSearch(pendingChanges, latestFieldValue[i]);
                }
            } else {
                pendingChanges = updatePendingChangesForSearch(pendingChanges, latestFieldValue);
            }
        }

        let searchField = undefined;
        if (event && event.target) {
            searchField = event.target.name;
        }

        if (windowSize.width <= 785) {
            setScrollTop(0);
        }

        performSearch(buildUrl(keepPage, pendingChanges, searchField), keepPage);
    };

    const fetchMapData = (searchUrl) => {
        setMapDataLoading(true);
        const queryString = searchUrl.indexOf('?') > -1 ? searchUrl.substring(searchUrl.indexOf('?')) : '';
        const mapDataUrl = props.EmptyLegsMapDataUrl + queryString;

        sandhillsRequest({
            url: mapDataUrl,
            method: 'post',
        }).then((data) => {
            setSearchData(prev => ({
                ...prev,
                FlightPaths: data.FlightPaths,
                ListingLocations: data.ListingLocations
            }));
            setMapDataLoading(false);
        }).catch(() => {
            setMapDataLoading(false);
        });
    };

    const performSearch = (newSearchUrl, skipMapDataFetch) => {
        setSearchInProgress(true);
        setOutstandingChanges([]);
        setEmptyLegsSearchUrl(newSearchUrl);
        sandhillsRequest({
            url: newSearchUrl,
            method: 'post',
        }).then((data) => {
            handleResponse(data);
        });

        if (!skipMapDataFetch) {
            fetchMapData(newSearchUrl);
        }
    };

    const handleResponse = (result) => {
        setSearchData(prev => ({
            ...prev,
            SearchBaseURL: result.EmptyLegsUrl,
            EmptyLegs: result.EmptyLegs,
            SearchFields: result.SearchFields,
            ListingsCount: result.ListingsCountTitle,
            PageHeader: result.PageHeader,
            CurrentPage: result.PaginationModel.CurrentPage,
            TotalPages: result.PaginationModel.TotalPages,
            IsDefaultSearch: result.IsDefaultSearch,
            LocationString: result.LocationString,
            ListingMapData: result.ListingMapData,
            DefaultZoom: result.DefaultZoom,
            DefaultCenter: result.DefaultCenter,
        }));

        setEmptyLegsSearchUrl(result.EmptyLegsSearchUrl);
        setSearchInProgress(false);
        document.title = result.PageTitle;
        window.history.pushState('', '', result.EmptyLegsUrl);
        syncCanonicalUrl(result.EmptyLegsUrl);
        setShowLoadingRing(false);
    };

    const handlePageNumberChange = (page) => {
        setShowLoadingRing(true);
        setSearchData(previousState => {
            return { ...previousState, CurrentPage: page };
        });
        setScrollTop(0);
    };

    const handleSetMapCenter = (lat, lng, distance) => {
        if (locationMapRef.current) {
            locationMapRef.current.setMapCenter(lat, lng, distance);
        }
    }

    const mapViewToggle = () => {
        setShowMapViewModal(showMapViewModal => !showMapViewModal);
    };

    const toggleSearchWindow = () => {
        if (windowSize.isMobile) {
            if (showSearchWindow) {
                removeIOSScrollLock(true);
            } else {
                setIOSScrollLock(true);
            }
        }
        let showWindow = !showSearchWindow;
        if (showWindow) {
            document.getElementsByTagName('body')?.[0]?.classList.add('fixed');
        } else {
            document.getElementsByTagName('body')?.[0]?.classList.remove('fixed');
        }

        setShowSearchWindow(showWindow);
        setMobileSearch(showWindow && window.innerWidth <= 785)
    };

    const clearAll = () => {
        setScrollTop(0);
        setSearchInProgress(true);
        const clearedUrl = props.EmptyLegsSearchUrl.split('?')[0];
        setEmptyLegsSearchUrl(clearedUrl);
        setSearchData(previousState => {
            return { ...previousState, SelectedSortValue: 1 };
        });

        sandhillsRequest({
            url: clearedUrl,
            method: 'post'
        }).then((data) => {
            handleResponse(data)
        });

        fetchMapData(clearedUrl);

        window.history.pushState('', '', searchData.SearchBaseURL.split('?')[0]);
        syncCanonicalUrl(searchData.SearchBaseURL.split('?')[0]);
    };

    const clearFacet = (fieldName, option) => {
        let searchUrl = buildUrl(false);
        //copy field name so we don't mutate 
        let queryStringParamName = (' ' + fieldName).slice(1);
        let selectedField = searchData.SearchFields.filter(f => f.FieldName == fieldName)[0];

        if (fieldName == constants.Distance) {
            searchUrl = removeURLParameter(searchUrl, constants.Distance);
            searchUrl = removeURLParameter(searchUrl, constants.CityID);
            searchUrl = removeURLParameter(searchUrl, constants.AirportID);

            //reset default map
            handleSetMapCenter(40.8058, -96.6718, 0);
        }

        if (option) {
            let qsValues = getURLParameter(searchUrl, queryStringParamName).split('|');
            searchUrl = removeURLParameter(searchUrl, queryStringParamName);

            let filter = qsValues;
            let index = qsValues.indexOf(option.Value ? option.Value : option);
            if (index > -1) {
                filter.splice(index, 1);
            }

            if (filter.length > 0 && filter[0] !== "") {
                searchUrl = insertURLParameter(searchUrl, queryStringParamName, filter.join('|'));
            }
        } else {
            searchUrl = removeURLParameter(searchUrl, queryStringParamName);
        }

        performSearch(searchUrl);
    };

    const clearFacetDebounced = useMemo(() => debounce(clearFacet, 300), [clearFacet]);

    const onEmptyLegQuoteModalToggle = () => {
        setShowEmptyLegQuoteModal(showEmptyLegQuoteModal => !showEmptyLegQuoteModal);
        if (returnFocusRef && returnFocusRef.current) {
            returnFocusRef.current.focus();
        }
    };

    const openEmptyLegQuoteModal = (modalData) => {
        modalData = modalData || {};
        const { ref: returnRef, ...modelData } = modalData;
        setShowEmptyLegQuoteModal(showEmptyLegQuoteModal => !showEmptyLegQuoteModal);
        setEmptyLegModel({ ...emptyLegModel, ...modelData });
        setReturnFocusRef(returnRef);
    };

    const handleMapRequestQuote = (flightPath) => {
        openEmptyLegQuoteModal({
            EmptyLegListingID: flightPath.EmptyLegListingID,
            DepartureFromField: flightPath.DepartureLocation,
            DepartureToField: flightPath.ToLocation,
            departingDate: flightPath.DepartureDateTime,
            AircraftDescription: flightPath.AircraftDescription,
            Phone: flightPath.AccountPhone,
            DepartureFromCityField: flightPath.DepartureFromCityField,
            DepartureFromStateField: flightPath.DepartureFromStateField,
            DepartureFromCountryID: flightPath.DepartureFromCountryID,
            DepartureToCityField: flightPath.DepartureToCityField,
            DepartureToStateField: flightPath.DepartureToStateField,
            DepartureToCountryID: flightPath.DepartureToCountryID,
        });
    };

    const scrollToFAQSection = () => {
        if (FAQSectionRef.current) {
            FAQSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    const handleAccordionChange = (panel) => (event, newExpanded) => {
        setExpanded(newExpanded ? panel : false);
    };

    const handleAlertSaved = (newAlert) => {
        setEmptyLegAlerts((previousState) => {
            const index = previousState.findIndex(a => a.EmptyLegAlertID === newAlert.EmptyLegAlertID);
            if (index >= 0) {
                previousState[index] = newAlert;
                return [...previousState];
            } else {
                return [...previousState, newAlert];
            }
        });
    };

    return (
        <div className='empty-legs-listings'>
            {showLoadingRing && <LoadingRing sxBox={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0, 0, 0, 0.4)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999, }} />}
            <section className='empty-legs-top-section'>
                <SimpleHomeBreadcrumb homeRoute={props.BreadCrumbHomeRoute} displayText={searchData.PageHeader} />
                <div className='page-title'>
                    <h1 className='empty-legs-title'>
                        <strong><span>{searchData.PageHeader}</span></strong>
                        <span className='empty-listings-count'><span dangerouslySetInnerHTML={{ __html: searchData.ListingsCount }}></span></span>
                    </h1>
                </div>
                {props.ShowEmptyLegsButton &&
                    <a id='post-empty-leg-button' className='post-empty-leg-button' href={props.PostEmptyLegUrl}>
                        <i className='fas fa-plus' /><h2 className='rac-text'>{translator.translate('sandhills.postemptylegs')}</h2>
                    </a>
                }
                {currentUser?.IsAuthenticated ?
                    <button className='post-empty-leg-button' onClick={() => setShowAlertModal(true)}>
                        <i className="fa-solid fa-bell"></i> <h2 className='rac-text'>Set Up Alerts</h2>
                    </button>
                    :
                    <a className='post-empty-leg-button' href={routes.EmptyLegsAlerts} target='_self'>Set Up Alerts</a>
                }
                {props.ProductBlurbText &&
                    <div className='product-blurb-summary'>
                        <p>{props.ProductBlurbText}</p>
                        <a role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); scrollToFAQSection(); } }} className='read-more' onClick={scrollToFAQSection}><TranslationText tKey='sandhills.readmore' /></a>
                    </div>}
            </section>
            <section className="list-bottom-section">
                <div className='side-container'>
                    <FacetedSearch
                        {...searchData}
                        runSearch={runSearch}
                        clearAll={clearAll}
                        clearFacet={clearFacetDebounced}
                        disabled={searchInProgress}
                        showMobileFilters={showSearchWindow}
                        toggleMobileFilter={toggleSearchWindow}
                        mobileSearch={mobileSearch}
                        UseSEOHTagReorganization={props.UseSEOHTagReorganization}
                        setShowLoadingRing={setShowLoadingRing}
                    />
                </div>
                <div className="map-listings-container">
                    {!windowSize.isMobile &&
                        <LocationMap locations={searchData.ListingLocations} listingMapData={searchData.ListingMapData} onLocationChange={setLocationState} defaultCenter={searchData.DefaultCenter} defaultZoom={searchData.DefaultZoom} ref={locationMapRef} flightPaths={searchData.FlightPaths} isOpen={true} currentUser={currentUser} onRequestQuote={handleMapRequestQuote} loading={mapDataLoading} />
                    }
                    <div id='main-content' className='empty-legs-content'>
                        <div className='listings-wrapper'>
                            {searchData.EmptyLegs && searchData.EmptyLegs.length > 0 ?
                                searchData.EmptyLegs.map((item, index) =>
                                    <EmptyLegListing
                                        {...item}
                                        key={item.EmptyLegListingID}
                                        FormattedLocation={searchData.LocationString}
                                        IsEmptyLegsDirectory={props.IsEmptyLegsDirectory}
                                        openEmptyLegQuoteModal={openEmptyLegQuoteModal}
                                        ShowEmployeeOnlyInfo={props.ShowEmployeeOnlyInfo}
                                        NoImageRow={listingPictureMap[index] ?? item.NoImageRow}
                                        IsFirst={index === 0}
                                        searchData={searchData}
                                    />
                                ) :
                                <div className='no-listings-filter'>
                                    <div><TranslationText tKey='sandhills.tradesites.mvc.sorryyouhavefilteredoutallemptylegs' /></div>
                                    <div>
                                        <TranslationText tKey='sandhills.tradesites.mvc.adjustyourfiltersorperformanewsearchbyselecting' /> '
                                        <a role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); clearAll(); } }} className='clear-all-text' onClick={clearAll}><TranslationText tKey='sandhills.clearall' /></a>'
                                    </div>
                                </div>
                            }
                        </div>
                        {searchData.TotalPages > 1 &&
                            <Paging
                                currentPage={searchData.CurrentPage}
                                totalPages={searchData.TotalPages}
                                onChange={handlePageNumberChange}
                            />}
                    </div>
                </div>
                <div className='clear' />
            </section>
            {(props.EmptyLegFAQs && props.EmptyLegFAQs.length > 0) && 
                <section className='faq-accordion-section' ref={FAQSectionRef}>
                    <div className='content'>
                        <h2 className='section-title'>{translator.translate('sandhills.tradesites.mvc.emptylegfaqs')}</h2>
                        <div className='accordion-container'>
                            {props.EmptyLegFAQs.map((faq, index) =>
                                <Accordion
                                    className={'accordion'}
                                    disableGutters
                                    expanded={expanded === 'panel' + index}
                                    onChange={handleAccordionChange('panel' + index)}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon className={'expand-icon'} />}
                                        className={'accordion-header'}
                                        aria-controls={'panel' + index + '-content'}
                                    >
                                        <strong>{faq.Question}</strong>
                                    </AccordionSummary>
                                    <AccordionDetails
                                        className={'accordion-content'}
                                    >
                                        {faq.Answer}
                                    </AccordionDetails>
                                </Accordion>)
                            }
                        </div>
                    </div>
                </section>
            }
            <FacetedSearchMobileMenu
                filter={{
                    render: true,
                    toggle: toggleSearchWindow
                }}
                PostEmptyLegUrl={props.PostEmptyLegUrl}
                ShowEmptyLegsButton={props.ShowEmptyLegsButton}
                setShowLoadingRing={setShowLoadingRing}
                IsEmptyLegsDirectory
            />
            {searchInProgress && <div className='searching-background' />}
            {showEmptyLegQuoteModal &&
                <EmptyLegQuoteModal
                    {...emptyLegModel}
                    toggleEmptyLegQuoteModal={onEmptyLegQuoteModalToggle}
                    IsEmployee={props.ShowEmployeeOnlyInfo}
                />}
            {showAlertModal &&
                <EmptyLegAlertModal
                    onClose={() => setShowAlertModal(false)}
                    onSave={handleAlertSaved}
                />
            }
            {currentSite.IsMobileAppContext &&
                <div className='detail__mobile-padding-bottom' />
            }
        </div>
    );
}

export default EmptyLegsListingsView;
global.EmptyLegsListingsView = EmptyLegsListingsView;